import { useMemo } from 'react'
import { usePages, useProjects , useTasks  } from '@/lib/db/rxdb/hooks'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'

const CLOSED_PROGRESS_TYPES = ['CL', 'CD']

export interface EntityLinkSuggestionItem {
  id: string
  label: string
  entityType: string // 'work_item' | 'document' | 'project'
  icon: string | null
}

// Module-level store for entity suggestions (same pattern as MentionExtension)
let entitySuggestions: Array<EntityLinkSuggestionItem> = []

export function setEntitySuggestions(items: Array<EntityLinkSuggestionItem>) {
  entitySuggestions = items
}

export function getEntitySuggestions(): Array<EntityLinkSuggestionItem> {
  return entitySuggestions
}

/**
 * Hook to sync workspace entities into the entity link extension's suggestion store.
 * Call this in any component that renders TiptapRichTextEditor with entity links enabled.
 */
export function useEntityLinkSuggestions() {
  const { tasks } = useTasks({ isDeleted: false })
  const { pages } = usePages()
  const { projects } = useProjects()

  useMemo(() => {
    const items: Array<EntityLinkSuggestionItem> = []

    for (const t of tasks) {
      if (t.isArchived || CLOSED_PROGRESS_TYPES.includes(t.status?.progressType ?? '')) continue
      items.push({
        id: t.id,
        label: t.name || 'Untitled task',
        entityType: ENTITY_TYPES.WORK_ITEM,
        icon: (t as any).identifier ?? null,
      })
    }

    for (const p of pages) {
      items.push({
        id: p.id,
        label: (p as any).title || 'Untitled page',
        entityType: ENTITY_TYPES.DOCUMENT,
        icon: (p as any).icon ?? null,
      })
    }

    for (const pr of projects) {
      items.push({
        id: pr.id,
        label: (pr as any).name || 'Untitled project',
        entityType: ENTITY_TYPES.PROJECT,
        icon: (pr as any).icon ?? null,
      })
    }

    setEntitySuggestions(items)
  }, [tasks, pages, projects])
}
